import { createSlice } from '@reduxjs/toolkit';

import initialState from './state';
import {
  fetchProjects,
  fetchWhitelistedProjects,
  fetchProjectDetails,
  fetchProjectSettings,
  fetchWorkerProfileCustomFields,
  fetchWorkerProfileFieldsForAllCompanies,
  fetchAppLanguages,
  fetchCompanies,
  fetchEthnicities,
  fetchLanguages,
  fetchCountries,
  fetchStates,
  fetchIdTypes,
  fetchSkilledTrades,
  fetchCertificationTypes,
  fetchConsentFormFields,
  fetchConsentAgreements,
  fetchJobTitles,
  fetchTradeStatus,
  fetchSocJobTitles,
  fetchLanguageTurnerProtocols,
  declineProject,
  fetchBadgePdf,
} from './actions';
import { logoutUser } from '../user';
import { fetchWorkerConsentAcceptances } from '../onboarding/actions';
import { canCreateUnverifiedWorkersInProject, getProfileFieldConstraintsMap } from 'utils';

const cacheSlice = createSlice({
  name: 'cache',
  initialState: initialState,
  reducers: {
    updateProfileFieldConstraintsMap: (state, action) => {
      const data = action.payload;
      state.profileFieldConstraintsMap = getProfileFieldConstraintsMap(data);
      state.canCreateUnverifiedWorkers = canCreateUnverifiedWorkersInProject(data || []);
    },
    setLastUpdatedAt: state => {
      state.lastUpdatedAt = new Date().toISOString();
    },
    restoreAdminCache: (state, action) => {
      return { ...state, ...action.payload };
    },
    resetCache: () => {
      return { ...initialState };
    },
  },
  extraReducers: builder =>
    builder
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.projects = action.payload || [];
      })
      .addCase(fetchWhitelistedProjects.fulfilled, (state, action) => {
        state.whitelistedProjectIds = action.payload || [];
      })
      .addCase(fetchProjectDetails.fulfilled, (state, action) => {
        state.project = action.payload || {};
      })
      .addCase(fetchProjectSettings.fulfilled, (state, action) => {
        state.projectSettings = action.payload || {};
      })
      .addCase(fetchWorkerProfileCustomFields.fulfilled, (state, action) => {
        if (action.payload.isProjectOnlyData) {
          state.projectProfileCustomFields = action.payload.data || [];
        }
        state.profileFieldConstraintsMap = getProfileFieldConstraintsMap(action.payload.data || []);
        state.canCreateUnverifiedWorkers = canCreateUnverifiedWorkersInProject(action.payload.data || []);
      })
      .addCase(fetchWorkerProfileFieldsForAllCompanies.fulfilled, (state, action) => {
        state.workerProfileFieldsForAllCompanies = action.payload || [];
      })
      .addCase(fetchAppLanguages.fulfilled, (state, action) => {
        state.appLanguages = { ...state.appLanguages, ...action.payload };
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companies = { ...state.companies, ...action.payload };
      })
      .addCase(fetchEthnicities.fulfilled, (state, action) => {
        state.ethnicities = { ...state.ethnicities, ...action.payload };
      })
      .addCase(fetchLanguages.fulfilled, (state, action) => {
        state.languages = { ...state.languages, ...action.payload };
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = { ...state.countries, ...action.payload };
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.states = { ...state.states, ...action.payload };
      })
      .addCase(fetchIdTypes.fulfilled, (state, action) => {
        state.iDTypes = { ...state.iDTypes, ...action.payload };
      })
      .addCase(fetchSkilledTrades.fulfilled, (state, action) => {
        state.skilledTrades = { ...state.skilledTrades, ...action.payload };
      })
      .addCase(fetchCertificationTypes.fulfilled, (state, action) => {
        state.certificationTypes = {
          ...state.certificationTypes,
          ...action.payload,
        };
      })
      .addCase(fetchConsentFormFields.fulfilled, (state, action) => {
        state.consentFormFields = {
          ...state.consentFormFields,
          ...action.payload,
        };
      })
      .addCase(fetchConsentAgreements.fulfilled, (state, action) => {
        state.consentAgreements = action.payload || [];
      })
      // Worker sessions skip the project-level fetch above (the API rejects it without a workerId),
      // so the worker-scoped call carries the documents instead. It only includes `agreements` for
      // workers — a reducer cannot read another slice, so the thunk makes that call and signals it by
      // the presence of the key. Admin sessions therefore keep this array owned by the fetch above.
      .addCase(fetchWorkerConsentAcceptances.fulfilled, (state, action) => {
        if (action.payload?.agreements) {
          state.consentAgreements = action.payload.agreements;
        }
      })
      .addCase(fetchJobTitles.fulfilled, (state, action) => {
        state.jobTitles = { ...state.jobTitles, ...action.payload };
      })
      .addCase(fetchTradeStatus.fulfilled, (state, action) => {
        state.tradeStatus = { ...state.tradeStatus, ...action.payload };
      })
      .addCase(fetchSocJobTitles.fulfilled, (state, action) => {
        state.socJobTitles = { ...state.socJobTitles, ...action.payload };
      })
      .addCase(fetchLanguageTurnerProtocols.fulfilled, (state, action) => {
        state.languageTurnerProtocol = {
          ...state.languageTurnerProtocol,
          ...action.payload,
        };
      })
      .addCase(declineProject.fulfilled, (state, action) => {})
      .addCase(fetchBadgePdf.fulfilled, (state, action) => {})
      .addCase(logoutUser.fulfilled, state => {
        state = initialState;
        return state;
      }),
});

export const { updateProfileFieldConstraintsMap, setLastUpdatedAt, restoreAdminCache } = cacheSlice.actions;

export default cacheSlice;
